import { type ReactElement, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';

import { ErrorPage } from '~/chords';
import { cn } from '~/lib/cn';
import { useUser } from '~/modules/auth/hooks/useUser';
import { useRemoteContexts } from '~/modules/remotes/hooks/use-remote-contexts';
import { useRemotes } from '~/modules/remotes/hooks/useRemotes';
import type { IRemoteDeclaration } from '~/modules/remotes/types';

import { useBrand } from '../branding';
import { ErrorBoundary } from '../errors/components/ErrorBoundary';
import { RemotesStatus } from './components/RemotesStatus';

interface PrivateRemoteProps {
  remote: IRemoteDeclaration;
}

export function PrivateRemote({ remote }: PrivateRemoteProps) {
  const remoteContexts = useRemoteContexts();
  const { hasPermission, ...user } = useUser();

  if (remote?.permission(hasPermission, { user })) {
    return (
      <Suspense>
        <div
          className={cn('contents', {
            'no-preflight': remoteContexts[remote.name].isolateStyles,
          })}
        >
          <remote.root ctx={remoteContexts[remote.name].context} />
        </div>
      </Suspense>
    );
  }

  return <ErrorPage type="noAccessPage" />;
}

PrivateRemote.displayName = 'PrivateRemote';

/**
 * Uniform Router.
 */
export function RemoteRouter() {
  const { name } = useBrand();
  const { remotes } = useRemotes();

  return (
    <ErrorBoundary>
      <Routes>
        {remotes.reduce<ReactElement[]>((acc, remote) => {
          if (!remote.brands.includes(name)) {
            return acc;
          }

          return [
            ...acc,
            <Route
              key={remote.name}
              path={remote.path[0]}
              errorElement={<ErrorPage />}
              element={<PrivateRemote remote={remote} />}
            />,
          ];
        }, [])}

        <Route path="_dev/remotes-status" element={<RemotesStatus />} />
      </Routes>
    </ErrorBoundary>
  );
}

RemoteRouter.displayName = 'RemoteRouter';
